import { createSlice } from "@reduxjs/toolkit";
// ⚠️ Deep-imports the DESKTOP variant on purpose. `userLogout`/`supportLogout` are web-only
// (defined in LoginSlice/index.desktop.ts, absent from index.native.ts), and a bare
// `../LoginSlice` typechecks against the NATIVE variant here: packages/core's tsconfig sets
// `moduleSuffixes: [".native", ".shared", ".desktop", ""]`, so `.native` wins for tsc
// regardless of which platform file is doing the importing. Every slice that resets on
// logout will need this same explicit import. CLAUDE.md sanctions it: a platform variant may
// import its matching variant explicitly.
import { supportLogout, userLogout } from "../LoginSlice";
import { sharedInitialState, sharedReducers } from "./shared";
import type { SharedMyOrderState } from "./shared";

/**
 * WEB half of MyOrderSlice.
 *
 * Nearly everything moved to `shared.ts` in the web-adoption pass — the 17 web-only reducers
 * and the state they write are now on both platforms. What is left here is what web alone can
 * or should run; see shared.ts's docstring for why each one stayed.
 */
export type MyOrderState = SharedMyOrderState;

const initialState: MyOrderState = { ...sharedInitialState };

export const myOrderSlice = createSlice({
  name: "myorder",
  initialState,
  reducers: {
    ...sharedReducers, // incl. resetMyOrders — converged onto this platform's body (D1)
  },
  // BLOCKED, not permanent: `userLogout`/`supportLogout` do not exist in LoginSlice's native
  // variant, so mobile cannot reference them. Both bodies are empty in the app source — the
  // cases exist but reset nothing — so converging them would buy nothing even once unblocked.
  // Preserved as-is; giving them a body would be a behaviour change, not a fix.
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {

      })
      .addCase(supportLogout, (state) => {

      });
  },
});

export const {
  getNewOrderData,
  getNewOrderDataSuccess,
  getPreparingOrderData,
  getPreparingOrderDataSuccess,
  getProcessedOrderData,
  getProcessedOrderDataSuccess,
  getDispatchedOrderData,
  getDispatchedOrderDataSuccess,
  getDeliveredOrderData,
  getDeliveredOrderDataSuccess,
  getCancelledOrderData,
  getCancelledOrderDataSuccess,
  getOpenOrderData,
  getOpenOrderDataSuccess,
  getOverviewOrderData,
  getOverviewOrderDataSuccess,
  resetMyOrders,
  getSearchOrders,
  getSearchOrdersSuccess,
  resetSearchOrders,
  updateOrderStatus,
  updateOrderStatusSuccess,
  fetchOrderData,
  fetchOrderDataSuccess,
  updateSoldOutItem,
  updateSoldOutSuccess,
  aggregatorDataUpdate,
  aggregatorDataUpdateSuccess,
  fetchHoldOrder,
  fetchHoldOrderSuccess,
  getCallBtnApi,
  getCallBtnApiSuccess,
  saveMyorderSuccess,
  saveMyorder,
  voidOrder,
  unvoidOrder,
  uncancelledOrder,
  swiggyCallBtnApi,
  swiggyCallBtnSucess,
  fetchFeebackLink,
  fetchFeebackLinkSuccess,
  generateAggregatorBill,
  updateFulfillmentStatus,
  checkDeliveryPartner,
  assignRider,
  cancelRider,
  fetchApprovalStatus,
  updateMyOrderSlice,
  getCustomerMaskedNumber,
  cancelOnlineOrder,
  getTotalSaleReturnAmount,
  getNewChatNotificationDetails,
  replaceChatSupportMessage,
  getChatSupportUrl,
  getChatSupportUrlSuccess,
  replaceChatSupportData,
  removedChatSupportId,
  checkForZomatoChatSupport,
  saveZomatoChatSupportList,
  getItemImageForOnlineOrder,
  getPreparedOrderData,
  getPreparedOrderDataSuccess,
} = myOrderSlice.actions;
export default myOrderSlice.reducer;
